import React, { useRef, useEffect, useState, useCallback } from 'react';
import { linkHorizontal, linkVertical } from 'd3-shape';
import { select } from 'd3-selection';

import { isHorizontal, transformToOrientation } from '../utils';
import { defaultProps, propTypes } from './propTypes';

function Link(props) {
  const { linkData, orientation, pathFunc, transitionDuration, enableLegacyTransitions, onClick, onMouseOver, onMouseOut, pathClassFunc } =
    props;

  const linkRef = useRef(null);
  const [initialStyle] = useState({ opacity: 0 });

  const applyOpacity = useCallback(
    (opacity, duration, done = () => {}) => {
      if (enableLegacyTransitions) {
        select(linkRef.current)?.transition?.()?.duration?.(duration)?.style?.('opacity', opacity)?.on?.('end', done);
      } else {
        select(linkRef.current)?.style?.('opacity', opacity);
        done();
      }
    },
    [enableLegacyTransitions]
  );

  // On mount, fade in
  useEffect(() => {
    applyOpacity(1, transitionDuration);
    // No cleanup needed for mount effect
  }, [applyOpacity, transitionDuration]);

  const drawStepPath = (linkData, orientation) => {
    const { source, target } = linkData;
    const step = (target.y - source.y) / 2;
    const tSource = transformToOrientation(source, orientation);
    const tTarget = transformToOrientation(target, orientation);
    const { x: mX, y: mY } = transformToOrientation({ x: 1, y: 1 }, orientation);

    return isHorizontal(orientation)
      ? `M${tSource.x},${tSource.y} H${tSource.x + mX * step} V${tTarget.y} H${tTarget.x}`
      : `M${tSource.x},${tSource.y} V${tSource.y + mY * step} H${tTarget.x} V${tTarget.y}`;
  };

  const drawDiagonalPath = (linkData, orientation) => {
    const { source, target } = linkData;
    const tSource = transformToOrientation(source, orientation);
    const tTarget = transformToOrientation(target, orientation);
    const func = isHorizontal(orientation) ? linkHorizontal() : linkVertical();

    return func({
      source: [tSource.x, tSource.y],
      target: [tTarget.x, tTarget.y],
    });
  };

  const drawStraightPath = (linkData, orientation) => {
    const { source, target } = linkData;
    const tSource = transformToOrientation(source, orientation);
    const tTarget = transformToOrientation(target, orientation);

    return `M${tSource.x},${tSource.y}L${tTarget.x},${tTarget.y}`;
  };

  const drawElbowPath = (linkData, orientation) => {
    const { source, target } = linkData;
    const tSource = transformToOrientation(source, orientation);
    const tTarget = transformToOrientation(target, orientation);

    return isHorizontal(orientation)
      ? `M${tSource.x},${tSource.y}V${tTarget.y}H${tTarget.x}`
      : `M${tSource.x},${tSource.y}H${tTarget.x}V${tTarget.y}`;
  };

  const drawPath = () => {
    if (typeof pathFunc === 'function') {
      return pathFunc(linkData, orientation);
    }
    if (pathFunc === 'elbow') {
      return drawElbowPath(linkData, orientation);
    }
    if (pathFunc === 'straight') {
      return drawStraightPath(linkData, orientation);
    }
    if (pathFunc === 'step') {
      return drawStepPath(linkData, orientation);
    }
    return drawDiagonalPath(linkData, orientation);
  };

  const getClassNames = () => {
    const classNames = ['rd3t-link'];

    if (typeof pathClassFunc === 'function') {
      classNames.push(pathClassFunc(linkData, orientation));
    }

    return classNames.join(' ')?.trim?.();
  };

  const handleOnClick = (evt) => {
    onClick(linkData.source, linkData.target, evt);
  };

  const handleOnMouseOver = (evt) => {
    onMouseOver(linkData.source, linkData.target, evt);
  };

  const handleOnMouseOut = (evt) => {
    onMouseOut(linkData.source, linkData.target, evt);
  };

  return (
    <path
      ref={linkRef}
      style={{ ...initialStyle }}
      className={getClassNames()}
      d={drawPath()}
      onClick={handleOnClick}
      onMouseOver={handleOnMouseOver}
      onMouseOut={handleOnMouseOut}
      data-source-id={linkData?.source?.data?.__rd3t?.id}
      data-target-id={linkData?.target?.data?.__rd3t?.id}
    />
  );
}

Link.propTypes = propTypes;
Link.defaultProps = defaultProps;

export default React.memo(Link);
